import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import useDebounce from '@/client/hooks/useDebounce';
import { useClientStore } from '@/client/stores/client';

type Hit = { eid: number; name: string; ffid: string | null };

/** 8-char ffids mix letters and digits. A letters-only string is a name. */
const isFfid = (s: string) => /^[a-z0-9]{8}$/i.test(s) && /[a-z]/i.test(s) && /\d/.test(s);

const hitsFromSearch = (raw: any): Hit[] => {
  const rows = raw?.entities ?? raw?.data?.entities ?? [];
  return rows
    .map((r: any) => ({
      eid: Number(r.eid ?? r.Entity?.eid),
      name: r.name || r.name_basic || r.Entity?.name || r.Entity?.name_basic || '',
      ffid: (r.ffid ?? r.Entity?.ffid ?? null) as string | null,
    }))
    .filter((h: Hit) => Number.isFinite(h.eid));
};

const hitFromEntity = (raw: any): Hit | null => {
  const data = raw?.data ?? raw;
  if (!data || data.error) return null;
  const e = data.Entity ?? data;
  const eid = Number(e?.eid);
  if (!Number.isFinite(eid)) return null;
  return { eid, name: e.name || e.name_basic || `#${eid}`, ffid: e.ffid ?? null };
};

/** Search listings by name, eid, or ffid and pick one. A typed eid is accepted directly. */
export const ListingPicker: React.FC<{ eid: number | null; onChange: (eid: number | null) => void }> = ({ eid, onChange }) => {
  const request = useClientStore((s) => s.request);
  const [text, setText] = React.useState('');
  const [pickedName, setPickedName] = React.useState<string | null>(null);
  const q = useDebounce(text.trim(), 300);
  const numeric = /^\d+$/.test(q);
  const exact = numeric || isFfid(q);

  const nameQuery = useQuery({
    queryKey: ['listing-picker', 'name', q],
    enabled: q.length >= 2 && !numeric,
    queryFn: () => request<any>(`/api/admin/entities/search?q=${encodeURIComponent(q)}&page_size=8`),
    retry: false,
  });

  const exactQuery = useQuery({
    queryKey: ['listing-picker', 'id', q],
    enabled: exact,
    queryFn: async () => {
      try {
        return await request<any>(`/api/v2/entity/${encodeURIComponent(q)}`);
      } catch {
        return null;
      }
    },
    retry: false,
  });

  const exactHit = exactQuery.data ? hitFromEntity(exactQuery.data) : null;
  const rows = [
    ...(exactHit ? [exactHit] : []),
    ...hitsFromSearch(nameQuery.data).filter((h) => h.eid !== exactHit?.eid),
  ];

  const exactEid = exactHit?.eid;
  const exactName = exactHit?.name;
  React.useEffect(() => {
    if (!exactEid) return;
    if (isFfid(q) || q === String(exactEid)) {
      onChange(exactEid);
      setPickedName(exactName ?? null);
    }
  }, [q, exactEid, exactName, onChange]);

  const onText = (v: string) => {
    setText(v);
    setPickedName(null);
    const t = v.trim();
    onChange(/^\d+$/.test(t) ? Number(t) : null);
  };

  const fetching = nameQuery.isFetching || exactQuery.isFetching;

  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium">Listing</span>
      <Input placeholder="Name, eid, or ffid" value={text} onChange={(e) => onText(e.target.value)} />
      {eid != null && (
        <p className="text-xs text-gray-500">
          Selected: #{eid}
          {pickedName ? ` — ${pickedName}` : ''}
        </p>
      )}
      <ul className="max-h-40 overflow-y-auto rounded-md border text-sm">
        {rows.map((r) => (
          <li key={r.eid}>
            <button
              type="button"
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 ${r.eid === eid ? 'bg-blue-50' : ''}`}
              onClick={() => {
                onChange(r.eid);
                setPickedName(r.name);
              }}
            >
              <span className="min-w-0 flex-1 truncate">{r.name || `#${r.eid}`}</span>
              <span className="shrink-0 font-mono text-xs text-gray-400">
                {r.ffid ? `${r.ffid} · ` : ''}#{r.eid}
              </span>
            </button>
          </li>
        ))}
        {!rows.length && (
          <li className="px-3 py-2 text-left text-xs text-gray-400">{fetching ? 'Searching…' : 'No listings found.'}</li>
        )}
      </ul>
    </div>
  );
};

export default ListingPicker;
